import { Box, Grid, Pagination } from "@mui/material";
import UnfocusedConversation from "components/Conversations/Views/Main/UnfocusedConversation/UnfocusedConversation";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import AcxEmptyState from "components/UI/AcxEmptyStateIndicator/AcxEmptyState";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import React from "react";
import {
    ConversationData,
    ConversationService,
} from "services/ConversationService";
import { ApplicationFilters } from "stores/ApplicationFilters/ApplicationFiltersStore";
import { DateReferenceOption } from "stores/ComponentStores/DatePickerComponentStore";
import { useStore } from "utils/useStore";

// Matches the backend's default page size for GetConversations.
const PAGE_SIZE = 10;

// Values are irrelevant: the backend ignores every other filter whenever
// ConversationIds is populated (see ConversationsController.GetConversations).
// This only exists to satisfy ApplicationFilters' required fields.
const EMPTY_APPLICATION_FILTERS: ApplicationFilters = {
    dateReferenceOption: DateReferenceOption.InteractionDate,
    evaluationTypes: [],
    hierarchyIds: [],
    mediaTypes: [],
    callDirections: [],
    containsTopics: false,
    containsPrimaryTopics: false,
};

type SortDirection = "asc" | "desc";

const sortOptions: { label: string; value: SortDirection }[] = [
    { label: "Newest First", value: "desc" },
    { label: "Oldest First", value: "asc" },
];

const conversationService = new ConversationService();

type Props = {
    /** Undefined while the ids are still being looked up. */
    conversationIds?: string[];
};

const FindingConversationsList: React.FC<Props> = ({ conversationIds }) => {
    const messageStore = useStore(MessageStore);
    const [pageNumber, setPageNumber] = React.useState(1);
    const [sortDirection, setSortDirection] =
        React.useState<SortDirection>("desc");
    const [results, setResults] = React.useState<{
        conversations: ConversationData[];
        count: number;
    }>();

    React.useEffect(() => {
        // An empty id list would match every conversation, so never send one.
        if (!conversationIds?.length) return;

        const abortController = new AbortController();
        conversationService
            .getConversations(
                {
                    ...EMPTY_APPLICATION_FILTERS,
                    conversationIds,
                    pageNumber,
                    sortField: "Timestamp",
                    sortDirection,
                },
                abortController.signal,
            )
            .then((payload) =>
                setResults({
                    conversations: payload.conversationResults,
                    count: payload.estimatedCount,
                }),
            )
            .catch(() => {
                if (abortController.signal.aborted) return;
                messageStore.logError("Failed to load conversations");
                setResults({ conversations: [], count: 0 });
            });

        return () => abortController.abort();
    }, [conversationIds, pageNumber, sortDirection, messageStore]);

    const count = conversationIds?.length === 0 ? 0 : results?.count;
    const totalPages = Math.ceil((count ?? 0) / PAGE_SIZE);

    return (
        <Grid container>
            <Grid
                container
                alignContent="center"
                justifyContent="space-between"
                sx={{ paddingBottom: 2 }}
            >
                <Box
                    fontWeight="bold"
                    fontSize="24px"
                    alignItems="center"
                    display="flex"
                >
                    {`Conversations (${count?.toLocaleString() ?? "..."})`}
                </Box>
                {!!count && (
                    <Grid item width="fit-content">
                        <AcxSelectSingle
                            id="kbo-finding-conversations-sort"
                            options={sortOptions}
                            valueField="value"
                            labelField="label"
                            defaultValue={sortOptions[0]}
                            menuPlacement="bottom"
                            onChange={(option) => {
                                setSortDirection(option.value);
                                setPageNumber(1);
                            }}
                            placeholder="Sort by"
                            width="150px"
                        />
                    </Grid>
                )}
            </Grid>

            {!count && (
                <Grid item xs={12}>
                    <AcxEmptyState
                        size="large"
                        variant={count === undefined ? "loading" : "empty"}
                    />
                </Grid>
            )}

            {!!count &&
                results?.conversations.map((conversation) => (
                    <Grid item xs={12} key={conversation.conversationId}>
                        <UnfocusedConversation conversation={conversation} />
                    </Grid>
                ))}

            {totalPages > 1 && (
                <Grid
                    container
                    item
                    xs={12}
                    justifyContent="flex-end"
                    sx={{ margin: "20px 0px" }}
                >
                    <Pagination
                        variant="outlined"
                        color="primary"
                        count={totalPages}
                        page={pageNumber}
                        onChange={(_, value) => setPageNumber(value)}
                    />
                </Grid>
            )}
        </Grid>
    );
};

export default FindingConversationsList;
